import { put, select, call } from 'redux-saga/effects';
import { toast } from 'react-toastify';
import { ascend, clone, concat, equals, find, findIndex, filter, isNil, isEmpty, insert, includes, join, pickBy, prop, propEq, reduce, replace, slice, sortBy, sortWith, toUpper, values } from 'ramda';
import moment from 'moment';
import queryString from 'query-string';
import { AppActions, DevelopActions, ManageActions } from '~/actions';
import { history } from '~/reducers';
import { SPECIALOPTIONS } from '~/services/config';
import { convertUrl, isNotNull, localDate } from '~/services/util';
import decamelize from '~/services/decamelize';
import NavigationService from '~/services/NavigationService';

export function* getcareerprogramRequest(api, action) {
    try {
        const { programId = null, route, userId = null } = action;
        let response = {};

        if (userId) { 
            const programs = yield api.getUserPrograms(userId);
            if (programs.ok && !isNil(programs?.data?.data)) {
                response = yield api.getCareerProgram(programs?.data?.data[0]?.id);
            }
        } else{
            response = yield api.getCareerProgram(programId);
        }

        if (response.ok) {
            const { program } = response.data;
            let user = {};
            if (!isNil(program.user_id)) {
                const employees = yield select((state) => state.app.users);
                user = find(propEq('id', program.user_id), employees) || {};
            }
            yield put(ManageActions.getcareerprogramSuccess({ program, profile: user.profile || {} }));
            if (route) yield call(NavigationService.push, route);
        } else {
            yield put(ManageActions.getcareerprogramFailure());
        }
    } catch (e) {
        yield put(ManageActions.getcareerprogramFailure());
    }
}

export function* postcareerprogramRequest(api, action) {
    try {
        const { payload } = action;
        const response = yield api.postCareerProgram(payload);

        if (response.ok) {
            yield put(ManageActions.postcareerprogramSuccess());
        } else {
            yield put(ManageActions.postcareerprogramFailure());
        }
    } catch (e) {
        yield put(ManageActions.postcareerprogramFailure());
    }
}

export function* gettasksfeedRequest(api, action) {
    const { userId, payload } = action;
    const companyId = yield select((state) => state.app.company_info.id);
    const options = queryString.stringify(decamelize(payload));

    try {
        const response = {};

        const tasksResponse = yield api.getTasksFeed(userId, options);
        if (tasksResponse.ok) {
            tasksResponse.data.data.sort(function (x, y) {
                return x.data.due_date - y.data.due_date;
            });
            for (let task of tasksResponse.data.data) {
                if (task.data.performance_review_task) {
                    const userId = task?.user_id;
                    const dateStart = moment(task.data.year_month).startOf('month').format('YYYY-MM-DD');
                    const dateEnd = moment(task.data.year_month).endOf('month').format('YYYY-MM-DD');
                    const _options = `user_id=${userId}&company_id=${companyId}&date_start=${dateStart}&date_end=${dateEnd}`;
                    const performanceResponse = yield api.getIndividualPerformanceReview(_options);
                    if (performanceResponse.ok) task.performance = performanceResponse.data;
                    else task.performance = { scorecards: [], performance: {} };
                }
            }
            response.tasksCard = tasksResponse.data;
        }

        let habitsData = [];
        const habitsResponse = yield api.getUserHabits(userId, options);
        if (habitsResponse.ok) habitsData = habitsResponse.data.data;

        const now = moment.utc().format('YYYY-MM-DD');
        const _options = `${options}&status[]=3&archived=1&completed_at_start=${now}`;
        const completedHabitsRes = yield api.getUserHabits(userId, _options);
        if (completedHabitsRes.ok) habitsData = concat(habitsData, completedHabitsRes.data.data);
        response.habitsCard = habitsData;

        const statsResponse = yield api.getUserTasksCounts(userId, options);
        if (statsResponse.ok) response.statsCard = statsResponse.data;
        yield put(ManageActions.gettasksfeedSuccess(response));
    } catch (e) {
        yield put(ManageActions.gettasksfeedFailure());
    }
}

export function* companyprojectsRequest(api, action) {
    const { companyIds, userIds } = action;
    const companies = yield select((state) => state.app.companies);
    const options = queryString.stringify({ company_id: companyIds }, { arrayFormat: 'bracket' });
    const response = yield api.getProjectStats(options);
    if (response.ok) {
        try {
            response.data.map((project) => {
                const company = find(propEq('id', project.company_id), companies);
                project.name = `${project.name} (${company.name})`;
            });
            const userId = yield select((state) => state.app.id);
            const tasksStatsRes = yield api.getUserTasksCounts(userId);
            const { adhoc_stats, recurring_types, recurring_open } = tasksStatsRes.data;
            const mytasks = {
                company_id: 0,
                data: null,
                id: 0,
                name: 'My Tasks & Habits',
                task_stats: { ...adhoc_stats },
                habit_stats: { ...recurring_types },
                open_stats: recurring_open + adhoc_stats.pending,
            };
            const assignedtasks = {
                company_id: 0,
                id: SPECIALOPTIONS.SELFASSIGNED,
                name: 'Assigned by me',
            };
            const tempList = insert(0, assignedtasks, response.data);
            const projectList = insert(0, mytasks, tempList);
            const selectedProject = yield select((state) => state.manage.selectedProject);
            const index = findIndex(propEq('id', selectedProject.id), projectList);
            yield put(ManageActions.companyprojectsSuccess(projectList));
            if (index > -1) yield put(ManageActions.selectprojectRequest(projectList[index], userIds));
            else yield put(ManageActions.selectprojectRequest(projectList[0], userIds));
        } catch (err) {
            yield put(ManageActions.companyprojectsFailure());
        }
    } else {
        yield put(ManageActions.companyprojectsFailure());
    }
}

export function* selectusersRequest(api, action) {
    try {
        const { payload } = action;
        const project = yield select((state) => state.manage.selectedProject);
        if (!isEmpty(project)) {
            const request = { projectId: project.id, users: payload, project };
            yield put(ManageActions.userprojecttaskRequest(request));
        }
    } catch (e) {
        yield put(ManageActions.userprojecttaskRequest(request));
    }
}

export function* userprojecttaskRequest(api, action) {
    try {
        const { payload } = action;
        const { projectId, users, completionType, project } = payload;

        if (projectId === SPECIALOPTIONS.SELFASSIGNED) {
            const option = 'card_type_id[]=8&per_page=5000';
            const response = yield api.getAssignedCards(option);
            if (response.ok) {
                response.data.data.forEach((data) => {
                    data.due_at = isNil(data.due_at) ? localDate(data.data.due_date, 'YYYY-MM-DD HH:MM:SS') : data.due_at;
                });
                const { data } = response.data;

                const tasksList = filter((data) => data.type === 'single' || data.user_id !== data.assigned_by, data);
                const completedTasksList = filter(({ status }) => status === 3, tasksList);
                const habitsList = filter(({ data }) => data.type !== 'single', data);
                const completedHabitsList = filter(({ status }) => status === 3, habitsList);
                const tasks = sortBy(prop('due_at'), tasksList);
                const completedTasks = sortBy(prop('due_at'), completedTasksList);
                const habits = sortBy(prop('due_at'), habitsList);
                const completedHabits = sortBy(prop('due_at'), completedHabitsList);
                yield put(ManageActions.userprojecttaskSuccess({ tasks, habits }));
                yield put(
                    ManageActions.userprojecttaskSuccess({
                        completionType: 'today',
                        tasks: completedTasks,
                        habits: completedHabits,
                    })
                );
            } else {
                yield put(ManageActions.userprojecttaskFailure());
            }
        } else {
            const userId = isNil(users) || isEmpty(users) || users[0] === -1 ? yield select((state) => state.app.id) : users[0];
            if (!isNil(userId)) {
                if (!isNil(project) && !isNil(project.name) && project.name === 'Approval') {
                    const res = yield api.getLearnFeeds('approvals', userId, '');
                    if (res.ok) {
                        const tasks = sortBy(prop('due_at'), res.data.data);
                        yield put(ManageActions.userprojecttaskSuccess({ tasks, habits: [] }));
                    } else {
                        yield put(ManageActions.userprojecttaskFailure());
                    }
                } else {
                    const completedStart = completionType === 'today' ? moment.utc().format('YYYY-MM-DD') : moment.utc().format('YYYY-MM-01');
                    const completedEnd = moment.utc().format('YYYY-MM-DD');
                    const options = projectId === 0 ? 'per_page=100' : reduce((cc, x) => cc + `&user_id[]=${x}`, `project_id[]=${projectId}`, users) + '&per_page=100';
                    const queries = isNil(completionType) ? options : `${options}&completed_at_start=${completedStart}&completed_at_end=${completedEnd}`;
                    const completedQuery = isNil(completionType) ? `${queries}&status[]=3&archived=1&completed_at_start=${completedEnd}` : `${queries}&status[]=3&archived=1`;
                    const response = yield api.getUserProjectTasks(userId, queries);
                    const response1 = yield api.getUserProjecthabits(userId, queries);
                    const completedHabitsRes = yield api.getUserProjecthabits(userId, completedQuery);

                    if (response.ok || response1.ok) {
                        let tasksRes = response.ok ? response.data.data : [];
                        let habitsRes = response1.ok ? response1.data.data : [];
                        if (completedHabitsRes.ok) {
                            habitsRes = concat(habitsRes, completedHabitsRes.data.data);
                        }
                        tasksRes = sortBy(prop('due_at'), tasksRes);
                        habitsRes = sortBy(prop('due_at'), habitsRes);
                        yield put(
                            ManageActions.userprojecttaskSuccess({
                                completionType,
                                tasks: tasksRes,
                                habits: habitsRes,
                            })
                        );
                    } else {
                        yield put(ManageActions.userprojecttaskFailure());
                    }
                }
            } else {
                yield put(ManageActions.userprojecttaskFailure());
            }
        }
    } catch (e) {
        yield put(ManageActions.userprojecttaskFailure());
    }
}

export function* selectprojectRequest(api, action) {
    try {
        const { project, userIds } = action;
        const payload = { projectId: project.id, users: userIds || [], project };

        yield put(ManageActions.userprojecttaskRequest(payload));

        const { id, task_stats, habit_stats } = project;
        const taskStats = isNil(task_stats)
            ? {}
            : {
                  type: 'single',
                  title: 'Tasks:',
                  data: [
                      { label: 'Total Open', value: task_stats.total },
                      { label: 'Current', value: id > 0 ? task_stats.open : task_stats.pending },
                      { label: 'Completed', value: task_stats.complete },
                      { label: 'Past Due', value: task_stats.past_due },
                  ],
              };

        const habitStats = isNil(habit_stats)
            ? {}
            : {
                  type: 'recurring',
                  data: [
                      {
                          title: 'Daily open:',
                          data: [
                              { label: 'Currently open', value: habit_stats.day.pending },
                              { label: 'Completed today', value: habit_stats.day.complete },
                              { label: 'MTD completion', value: habit_stats.day.completion },
                          ],
                      },
                      {
                          title: 'Weekly open:',
                          data: [
                              { label: 'Currently open', value: habit_stats.week.pending },
                              { label: 'Completed today', value: habit_stats.week.complete },
                              { label: 'MTD completion', value: habit_stats.week.completion },
                          ],
                      },
                      {
                          title: 'Monthly open:',
                          data: [
                              { label: 'Currently open', value: habit_stats.month.pending },
                              { label: 'Completed today', value: habit_stats.month.complete },
                              { label: 'MTD completion', value: habit_stats.month.completion },
                          ],
                      },
                  ],
              };

        yield put(ManageActions.selectprojectSuccess({ ...project, taskStats, habitStats }));
    } catch (e) {
        yield put(ManageActions.selectprojectFailure());
    }
}

export function* fetchtaskdetailRequest(api, action) {
    try {
        const { cardInstanceId, mode } = action;
        const response = yield api.updateCardInstance(cardInstanceId);

        if (response.ok && !isNil(response.data)) {
            yield put(ManageActions.fetchtaskdetailSuccess(response.data));
            if (mode === 'Preview') {
                const course = response.data;
                yield put(
                    AppActions.modalRequest({
                        type: mode,
                        data: { before: { course, module: course.children[0], index: 0 } },
                    })
                );
                const _review = yield select((state) => state.manage.individualPerformanceReview);
                const review = clone(_review);
                const idx = findIndex((e) => e.id === course.id, review?.performance?.trainings || []);
                if (idx > -1) {
                    review.performance.trainings[idx] = course;
                    yield put(ManageActions.individualperformancereviewSuccess(review));
                }
            } else {
                let actuals = {};
                if (response.data.data.quota_actual_task === 'yes') {
                    const userId = response.data.user_id;
                    let year = moment(response.data.due_at).year();
                    let month = moment(response.data.due_at).month();
                    if (month === 0) {
                        month = 12;
                        year = year - 1;
                    }
                    const actualRes = yield api.getActualsCount(userId, month, year);
                    if (actualRes.ok) {
                        actuals = { actualData: actualRes.data };
                        const start = moment([year, month - 1]);
                        const end = moment(start).endOf('month');
                        const dateStart = start.format('YYYY-MM-DD');
                        const dateEnd = end.format('YYYY-MM-DD');
                        yield put(
                            DevelopActions.getlearnfeedsRequest(userId, 'quotas', {
                                dateStart,
                                dateEnd,
                                order: 'id',
                            })
                        );
                    }
                }
                const modalData = yield select((state) => state.app.modalData);
                const data = { before: { ...modalData.before, card: { ...response.data, ...actuals } } };
                yield put(AppActions.modalRequest({ type: null, data }));
            }
        } else {
            yield put(ManageActions.fetchtaskdetailFailure());
        }
    } catch (e) {
        yield put(ManageActions.fetchtaskdetailFailure());
    }
}

export function* updatetaskRequest(api, action) {
    try {
        const { event, cardId, after } = action;
        const response = yield api.updateCard(event, cardId);

        if (response.ok) {
            yield put(ManageActions.updatetaskSuccess({ event, cardId }));
            if (after) yield put({ ...after });
        } else {
            yield put(ManageActions.updatetaskFailure());
            toast.error('Oops, Your action cannot be completed!. Please try again', {
                position: toast.POSITION.TOP_RIGHT,
            });
        }
    } catch (e) {
        yield put(ManageActions.updatetaskFailure());
    }
}

// Edit Card
export function* edittaskRequest(api, action) {
    try {
        const userId = yield select((state) => state.app.id);
        const companyId = yield select((state) => state.app.company_info.id);

        const { data, taskId } = action.payload;
        const response = yield api.updateCardInstance(taskId, 'update', data);

        if (response.ok) {
            yield put(ManageActions.gettasksfeedRequest(userId, { page: 1, perPage: 100 }));
            yield put(ManageActions.companyprojectsRequest([companyId]));
            yield call(fetchTasksFromProjects, api, {});
            yield put(ManageActions.edittaskSuccess(null));
        } else {
            yield put(ManageActions.edittaskFailure());
        }
    } catch (e) {
        yield put(ManageActions.edittaskFailure());
    }
}

// Delete Card
export function* deletetaskRequest(api, action) {
    try {
        const { cardInstanceId, mode, after } = action;
        const userId = yield select((state) => state.app.id);
        const companyId = yield select((state) => state.app.company_info.id);
        const response = yield api.updateCardInstance(cardInstanceId, 'delete');

        if (response.ok) {
            toast.success('Your task was successfully deleted!', {
                position: toast.POSITION.TOP_RIGHT,
            });
            if (!isNil(after)) yield put({ ...after });
            yield put(ManageActions.deletetaskSuccess(response.data));
        } else {
            yield put(ManageActions.deletetaskFailure());
            toast.error('Oops, Your action cannot be completed!. Please try again', {
                position: toast.POSITION.TOP_RIGHT,
            });
        }
    } catch (e) {
        yield put(ManageActions.deletetaskFailure());
        toast.error('Oops, Your action cannot be completed!. Please try again', {
            position: toast.POSITION.TOP_RIGHT,
        });
    }
}

// Add Comment
export function* addmngcommentRequest(api, action) {
    try {
        const { payload, cardInstanceId } = action;
        const finalData = {
            comment: {
                card_instance_id: cardInstanceId,
                data: { body: payload },
            },
        };
        const companyId = yield select((state) => state.app.company_info.id);
        const response = yield api.addComment(finalData);

        if (response.ok) {
            yield put(ManageActions.addmngcommentSuccess(response.data));
            yield put(ManageActions.fetchtaskdetailRequest(cardInstanceId));
            yield put(ManageActions.companyprojectsRequest([companyId]));
        } else {
            yield put(ManageActions.addmngcommentFailure());
        }
    } catch (e) {
        yield put(ManageActions.addmngcommentFailure());
    }
}

export function* addtaskRequest(api, action) {
    try {
        const { payload, users, projectId } = action;
        const userId = yield select((state) => state.app.id);
        const attachment = yield select((state) => state.app.learnUploadURL);
        const currentCompanyInfo = yield select((state) => state.app.company_info);
        const employees = yield select((state) => state.app.users);

        let assignedUsers = '';

        for (const user_id of users) {
            if (user_id === userId) {
                assignedUsers += 'you' + ',';
            } else {
                const user = employees.filter((user) => user.id === user_id);
                assignedUsers += user[0].name + ',';
            }
        }
        assignedUsers = assignedUsers.replace(/,\s*$/, '');

        let splitResult = assignedUsers.split(',');
        let toastMessage = '';
        if (users.length === 1) toastMessage = assignedUsers;
        else if (users.length === 2) toastMessage = `${splitResult[0] + ' and ' + splitResult[1]}`;
        else if (users.length === 3) {
            toastMessage = `${splitResult[0] + ' , ' + splitResult[1] + ' and ' + splitResult[2]}`;
        } else if (users.length === 4) {
            toastMessage = `${splitResult[0] + ' , ' + splitResult[1] + ' , ' + splitResult[2] + ' and 1 other'}`;
        } else {
            toastMessage = `${splitResult[0] + ' , ' + splitResult[1] + ' , ' + splitResult[2] + ' and ' + (users.length - 3) + ' others'}`;
        }

        const cardData = {
            card: {
                card_template_id: null,
                card_type_id: 8,
                parent_id: null,
                data: {
                    ...payload.data,
                    promote_user_id: userId,
                    type: 'single',
                    company_id: currentCompanyInfo.id,
                    attachment,
                },
                project_id: projectId,
                due_at: payload.due_at,
            },
            user_id: users,
        };
        const response = yield api.createCard(cardData);
        yield put(AppActions.uploadSuccess(''));
        if (response.ok) {
            const currentRoute = history.location.pathname;
            yield put(AppActions.modalRequest({ type: '' }));
            yield put(ManageActions.addtaskSuccess(response.data));

            // refresh daily plan
            if (currentRoute === '/hcm/daily-plan') {
                yield gettasksfeedRequest(api, { userId, payload: { page: 1, perPage: 500 } });
            }
            // refresh tasks & projects page
            if (currentRoute === '/hcm/tasks-projects') {
                const currentUsers = yield select((state) => state.app.selectedEmployee.users);
                const userIds = currentUsers[0] === -1 ? employees.filter((item) => item.id !== null).map((item) => item.id) : currentUsers;
                const project = yield select((state) => state.manage.selectedProject);
                const payload = { projectId: project.id, users: userIds, project };
                yield put(ManageActions.userprojecttaskRequest(payload));
            }
            if (userId !== users[0] || users.length > 1 || !equals(currentRoute, '/hcm/daily-plan' || '/hcm/tasks-projects')) {
                toast.success('Your task has been assigned to ' + toastMessage, {
                    position: toast.POSITION.CENTRE_RIGHT,
                    autoClose: 2000,
                });
            }
        } else {
            yield put(ManageActions.addtaskFailure());
            toast.error('Oops, Your action cannot be completed!. Please try again', {
                position: toast.POSITION.CENTRE_RIGHT,
                autoClose: 2000,
            });
        }
    } catch (e) {
        yield put(ManageActions.addtaskFailure());
    }
}

export function* searchmodulesRequest(api, action) {
    try {
        const { payload } = action;
        const { author, department, competency, category, type, search } = payload;
        let options = 'per_page=100';
        if (author) options = `${options}&author_id[]=${author}`;
        if (department) options = `${options}&department_id[]=${department}`;
        if (competency) options = `${options}&competency_id[]=${competency}`;
        if (category) options = `${options}&category_id[]=${category}`;
        if (type === 'habit') options = `${options}&card_type_id[]=8`;
        if (search) options = `${options}&search=${search}`;
        const mode = type === 'quota' ? 'quota_templates' : 'modules';
        const response = yield api.getDevLibrarySearchModules(options, mode);
        if (response.ok) {
            const modules = response.data.data.map((course) => {
                course.data.thumb_url = convertUrl(course.data.thumb_url);
                return course;
            });

            const after = yield select((state) => state.app.modalData.after);
            yield put(ManageActions.searchmodulesSuccess(modules));
            yield put(
                AppActions.modalRequest({
                    type: null,
                    data: { before: { type, modules }, after },
                })
            );
        } else {
            yield put(ManageActions.searchmodulesFailure());
        }
    } catch (e) {
        yield put(ManageActions.searchmodulesFailure());
    }
}

export function* assigntrainingRequest(api, action) {
    try {
        const { payload, after, attendance, callback } = action;
        const dateStart = after?.payload?.dateStart || moment().subtract(1, 'months').startOf('month').format('YYYY-MM-DD');
        const dateEnd = after?.payload?.dateEnd || moment().subtract(1, 'months').endOf('month').format('YYYY-MM-DD');
        const cardType =
            payload.cardType === 'scorecards'
                ? 'scorecard'
                : payload.cardType === 'habitslist'
                  ? 'Habit Schedule'
                  : payload.cardType === 'program'
                    ? 'Program'
                    : payload.cardType === 'task'
                      ? 'Task'
                      : payload.cardType === 'powerpoint'
                        ? 'Powerpoint'
                        : replace(/^./, toUpper(slice(0, 1, payload.cardType)), slice(0, -1, payload.cardType));

        let response = {};
        if (cardType === 'scorecard') {
            if (payload.preAssignees) {
                let scorecard = [];
                for (const employee of payload.preAssignees) {
                    const { scorecards } = employee?.extra || {};
                    if (scorecards) {
                        const scorecardIds = scorecards.map((x) => x.id);
                        scorecard = concat(scorecard, scorecardIds);
                    }
                }
                for (const id of scorecard) {
                    yield api.postDeleteScorecard({ scorecard: { id } });
                }
            }
            response = yield api.assignScorecard(payload);
        } else if (payload.cardType === 'program') {
            response = yield api.postProgramEvent('start', payload.data);
        } else {
            response = yield api.assignTraining(payload);
        }
        const users = yield select((state) => state.app.selectedEmployee.users);
        const user = users.length ? users[0] : yield select((state) => state.app.id);
        const userId = user < 0 ? yield select((state) => state.app.id) : user;
        const companyId = yield select((state) => state.app.company_info.id);

        if (response.ok) {
            if (response.data[0]?.original?.message) {
                toast.error(response.data[0]?.original?.message, {
                    position: toast.POSITION.CENTRE_RIGHT,
                });
                yield put(ManageActions.assigntrainingFailure());
                return;
            }
            if (attendance) {
                const { course, users } = attendance;
                const cards = filter(propEq('status', 3), course.children);
                const templates = cards.map((x) => x.id);
                for (const user of users) {
                    const options = `card_template_id[]=${course.id}`;
                    const res = yield api.getLearnFeeds('training', user, options);
                    if (res.ok) {
                        const training = res.data.data[0];
                        for (const childCard of training.children) {
                            if (includes(childCard.card_template_id, templates)) {
                                yield api.updateCard('completed', childCard.id);
                            }
                        }
                    }
                }
            }
            if (after) yield put({ ...after });
            toast.success(cardType + ' is assigned successfully', {
                position: toast.POSITION.CENTRE_RIGHT,
                pauseOnFocusLoss: false,
                hideProgressBar: true,
            });

            /**
             * We should remove after functionality slightly.
             * Currently, in case of scorecards, we don't need to post after parameter,
             * because it has been already reflected on the flow.
             */
            if (after && payload.cardType !== 'scorecards') {
                const { mode, card } = after;
                if (mode === 'reassign') {
                    if (payload.cardType === 'program') {
                        yield api.postProgramEvent('delete', { program: { id: payload.data.program_id } });
                    } else {
                        yield api.updateCardInstance(card.id, 'delete');
                    }
                }
                yield put({ ...after });
            }

            if (history.location.pathname === '/hcm/training-feed') {
                yield put(
                    DevelopActions.getlearnfeedsRequest(userId, 'courses', {
                        page: 1,
                        perPage: 10,
                        sort: 'ASC',
                    })
                );
            }
            if (history.location.pathname === '/hcm/daily-plan') {
                yield put(DevelopActions.dailytrainingRequest({ userId }));
            }
            if (history.location.pathname === '/hcm/tasks-projects') {
                const project = yield select((state) => state.manage.selectedProject);
                const request = { projectId: project.id, users: [userId], project };
                yield put(ManageActions.userprojecttaskRequest(request));
            }
            if (history.location.pathname === '/hcm/report-assignments') {
                yield put(
                    DevelopActions.getlearnfeedsRequest(
                        userId,
                        'tracks',
                        {
                            dateStart,
                            dateEnd,
                            page: 1,
                            perPage: 10,
                            order: 'type',
                            sort: 'DESC',
                        },
                        'status[]=1&status[]=2'
                    )
                );
            }
            if (callback) callback();

            yield put(ManageActions.assigntrainingSuccess(payload.cardType, response.data[0][payload.cardType]));
        } else {
            yield put(ManageActions.assigntrainingFailure());
            toast.error('Oops, Your action cannot be completed!. Please try again', {
                position: toast.POSITION.CENTRE_RIGHT,
            });
        }
    } catch (e) {
        yield put(ManageActions.assigntrainingFailure());
    }
}

export function* assigncoursesRequest(api, action) {
    try {
        const { payload } = action;
        const response = yield api.postLibraryTrackSave(payload);

        if (response.ok) {
            toast.success('Your training schedule has been assigned', {
                position: toast.POSITION.CENTRE_RIGHT,
            });
            yield call(NavigationService.push, '/hcm/reports');
            yield put(ManageActions.assigntrainingSuccess());
        } else {
            yield put(ManageActions.assigntrainingFailure());
        }
    } catch (e) {
        yield put(ManageActions.assigntrainingFailure());
    }
}

// Unassign Content
export function* unassigncontentRequest(api, action) {
    try {
        const { data, after } = action.payload;
        const response = yield api.postUnassignContent(data);

        if (response.ok) {
            if (!isNil(after)) yield put({ ...after });
            toast.success('Your content was successfully unassigned!', {
                position: toast.POSITION.TOP_RIGHT,
                pauseOnFocusLoss: false,
                hideProgressBar: true,
            });
            yield put(ManageActions.unassigncontentSuccess());
        } else {
            toast.error('Oops, Your action cannot be completed!. Please try again', {
                position: toast.POSITION.TOP_RIGHT,
                pauseOnFocusLoss: false,
                hideProgressBar: true,
            });
            yield put(ManageActions.unassigncontentFailure());
        }
    } catch (e) {
        yield put(ManageActions.unassigncontentFailure());
    }
}

// Add project
export function* addprojectRequest(api, action) {
    try {
        const { payload } = action;
        const companyId = yield select((state) => state.app.company_info.id);
        const response = yield api.postMngProjectsAddNew(payload.data, companyId);

        if (response.ok) {
            yield put(AppActions.modalRequest({ type: '' }));
            yield put(AppActions.postmulticompanydataRequest());
            yield put(ManageActions.addprojectSuccess(response.data));
        } else {
            yield put(ManageActions.addprojectFailure());
            toast.error('Oops, Your action cannot be completed!. Please try again', {
                position: toast.POSITION.TOP_RIGHT,
            });
        }
    } catch (e) {
        yield put(ManageActions.addprojectFailure());
    }
}

// Update project
export function* updateprojectRequest(api, action) {
    try {
        const { payload } = action;
        const currentCompanyInfo = yield select((state) => state.app.company_info);
        const response = yield api.postMngProjectsUpdate(payload.data, currentCompanyInfo.id, payload.id);
        if (response.ok) {
            yield put(AppActions.modalRequest({ type: '' }));
            yield put(ManageActions.companyprojectsRequest([currentCompanyInfo.id]));
            yield put(ManageActions.updateprojectSuccess(response.data));
        } else {
            yield put(ManageActions.updateprojectFailure());
            toast.error('Oops, Your action cannot be completed!. Please try again', {
                position: toast.POSITION.TOP_RIGHT,
            });
        }
    } catch (e) {
        yield put(ManageActions.updateprojectFailure());
    }
}

// Delete project
export function* deleteprojectRequest(api, action) {
    try {
        const { projectId } = action;
        const currentCompanyInfo = yield select((state) => state.app.company_info);
        const response = yield api.postMngProjectsDelete(projectId, currentCompanyInfo.id);
        if (response.ok) {
            yield put(ManageActions.deleteprojectSuccess(response.data));
            yield put(ManageActions.companyprojectsRequest([currentCompanyInfo.id]));
        } else {
            yield put(ManageActions.deleteprojectFailure());
            toast.error('Oops, Your action cannot be completed!. Please try again', {
                position: toast.POSITION.TOP_RIGHT,
            });
        }
    } catch (e) {
        yield put(ManageActions.deleteprojectFailure());
    }
}

function* addToTask(api, taskIds) {
    try {
        const currentCompanyInfo = yield select((state) => state.app.company_info);
        const data = [];
        for (const taskId of taskIds) {
            const response = yield call(api.getProjectTasks, currentCompanyInfo.id, taskId);
            if (response.ok) data.push(response.data[0]);
        }
        const currentProject = yield select((state) => state.manage.currentProject);
        if (currentProject === '') yield put(ManageActions.selectedprojectRequest(data[0].id));
        yield put(ManageActions.fetchprojectsdetailSuccess(data));
    } catch (e) {
        yield put(ManageActions.fetchprojectsdetailFailure());
    }
}

function* fetchTasksFromProjects(api, action) {
    const projects = yield select((state) => state.app.projects);
    const taskIds = projects.map((project) => project.id);

    try {
        yield call(addToTask, api, taskIds);
        yield put(ManageActions.fetchprojectdetailComplete());
    } catch (e) {
        yield put(ManageActions.projectdetailfetchFailure());
    }
}

export function* fetchprojectsdetailRequest(api, action) {
    yield call(fetchTasksFromProjects, api, action);
}

export function* updateProjectCards(api, action) {
    const { event, cardId } = action;
    yield put(ManageActions.updatetaskRequest(event, cardId));
}

export function* companyindividualRequest(api, action) {
    try {
        const { payload } = action;
        const response = yield api.getIndividualTasks(payload);

        if (response.ok) {
            let individuals = values(response.data.individuals);
            if (!isNil(payload.userId)) {
                individuals = filter((item) => payload.userId === item.id, individuals);
            }
            individuals = sortBy(prop('name'), individuals);
            const report = {
                companies: response.data.companies,
                individuals,
                totals: response.data.totals,
            };
            yield put(ManageActions.companyindividualSuccess(report));
        } else {
            yield put(ManageActions.companyindividualFailure());
        }
    } catch (e) {
        yield put(ManageActions.companyindividualFailure());
    }
}

export function* fetchengagementreportRequest(api, action) {
    try {
        const { payload, by = 'company', mode = 'trainings' } = action;
        const response = yield api.postMngReportsEngagementReport(payload, by, mode);

        const nameSort = sortWith([ascend(prop('name'))]);

        const data = {
            companies: nameSort(values(response.data.companies)),
            individuals: nameSort(values(response.data.individuals)),
            totals: [response.data.totals],
            type: mode,
            by,
        };

        if (response.ok) {
            yield put(ManageActions.fetchengagementreportSuccess(data));
        } else {
            yield put(ManageActions.fetchengagementreportFailure());
        }
    } catch (e) {
        yield put(ManageActions.fetchengagementreportFailure());
    }
}

export function* fetchcompanycareersRequest(api, action) {
    try {
        const { companyId } = action;
        const response = yield api.getCompanyCareers(companyId);

        if (response.ok) {
            yield put(ManageActions.fetchcompanycareersSuccess(response.data));
        } else {
            yield put(ManageActions.fetchcompanycareersFailure());
        }
    } catch (e) {
        yield put(ManageActions.fetchcompanycareersFailure());
    }
}

export function* companyperformancereviewsRequest(api, action) {
    try {
        const { payload } = action;
        const options = queryString.stringify(pickBy(isNotNull, decamelize(payload)), {
            arrayFormat: 'bracket',
        });
        const response = yield api.getCompanyPerformanceReviews(options);
        if (response.ok) {
            if (response.data.users) {
                response.data.users.forEach((item) => {
                    item.user.profile.avatar = convertUrl(item.user.profile.avatar, '/images/default.png');
                });
            } else {
                response.data.users = [];
            }
            yield put(ManageActions.companyperformancereviewsSuccess(response.data));
        } else {
            yield put(ManageActions.companyperformancereviewsFailure());
        }
    } catch (e) {
        yield put(ManageActions.companyperformancereviewsFailure());
    }
}

export function* companyperformancereviewsstatsRequest(api, action) {
    try {
        const { payload } = action;
        const options = queryString.stringify(pickBy(isNotNull, decamelize(payload)), {
            arrayFormat: 'bracket',
        });
        const response = yield api.getCompanyPerformanceReviewStats(options);
        if (response.ok) {
            yield put(
                ManageActions.companyperformancereviewsstatsSuccess({
                    stats: response.data.totals,
                    departments: values(response.data.departments),
                })
            );
        } else {
            yield put(ManageActions.companyperformancereviewsstatsFailure());
        }
    } catch (e) {
        yield put(ManageActions.companyperformancereviewsstatsFailure());
    }
}

export function* companydailycheckoutsRequest(api, action) {
    try {
        const { payload } = action;
        const options = queryString.stringify(pickBy(isNotNull, decamelize(payload)), {
            arrayFormat: 'bracket',
        });
        const response = yield api.getCompanyDailyCheckouts(options);
        if (response.ok) {
            if (response.data.users) {
                response.data.users.forEach((item) => {
                    item.user.profile.avatar = convertUrl(item.user.profile.avatar, '/images/default.png');
                });
            } else {
                response.data.users = [];
            }
            yield put(ManageActions.companydailycheckoutsSuccess(response.data));
        } else {
            yield put(ManageActions.companydailycheckoutsFailure());
        }
    } catch (e) {
        yield put(ManageActions.companydailycheckoutsFailure());
    }
}

export function* getcompanycheckoutsRequest(api, action) {
    try {
        const { payload, route } = action;
        const options = queryString.stringify(pickBy(isNotNull, decamelize(payload)), {
            arrayFormat: 'bracket',
        });
        const response = yield api.getReportCheckouts(options);
        if (response.ok) {
            if (response.data.users) {
                response.data.users.forEach((item) => {
                    item.user.profile.avatar = convertUrl(item.user.profile.avatar, '/images/default.png');
                });
            } else {
                response.data.users = [];
            }
            yield put(ManageActions.getcompanycheckoutsSuccess(response.data));
            if (route) yield call(NavigationService.push, route);
        } else {
            yield put(ManageActions.getcompanycheckoutsFailure());
        }
    } catch (e) {
        yield put(ManageActions.getcompanycheckoutsFailure());
    }
}

export function* individualperformancereviewRequest(api, action) {
    try {
        const { payload, route } = action;
        
        const options = queryString.stringify(decamelize(payload));
        
        const presponse = yield api.getIndividualPerformanceReview(options);
        const qresponse = yield api.getQuotasActuals(payload.userId, options);

        if (presponse.ok) {
            if (presponse.data.scorecards.length > 0) {
                presponse.data.scorecards[0].quotas = qresponse.data;
            }
            yield put(ManageActions.individualperformancereviewSuccess(presponse.data));
            yield put(ManageActions.getquotasactualsSuccess(qresponse.data));
            const { userId, companyId, dateStart, dateEnd } = payload;
            yield put(ManageActions.getpreviousreviewtasksRequest(presponse.data.performance?.id, userId, { companyId, dateStart, dateEnd }, false));
            if (route) yield call(NavigationService.push, route);
        } else {
            yield put(ManageActions.individualperformancereviewFailure());
        }
    } catch (e) {
        yield put(ManageActions.individualperformancereviewFailure());
    }
}

export function* postperformancereviewRequest(api, action) {
    try {
        const { payload } = action;
        const response = yield api.postPerformanceReview(payload);
        if (response.ok) {
            if (payload.performance.status === 2) yield call(NavigationService.goBack);
            yield put(ManageActions.postperformancereviewSuccess(response.data));
        } else {
            yield put(ManageActions.postperformancereviewFailure());
        }
    } catch (e) {
        yield put(ManageActions.postperformancereviewFailure());
    }
}

export function* postperformancereviewresetRequest(api, action) {
    try {
        const { payload, after } = action;
        const response = yield api.postPerformanceReviewReset(payload);
        if (response.ok) {
            yield put(ManageActions.postperformancereviewresetSuccess(response.data));
            if (after) yield put({ ...after });
        } else {
            yield put(ManageActions.postperformancereviewresetFailure());
        }
    } catch (e) {
        yield put(ManageActions.postperformancereviewresetFailure());
    }
}

export function* getperformancereviewsRequest(api, action) {
    try {
        const { id, payload, route } = action;
        const options = queryString.stringify(pickBy(isNotNull, payload));
        const response = yield api.getPerformanceReviews(id, options);

        if (response.ok) {
            yield put(ManageActions.getperformancereviewsSuccess(response.data));
            if (route) yield call(NavigationService.push, route);
        } else {
            yield put(ManageActions.getperformancereviewsFailure());
        }
    } catch (e) {
        yield put(ManageActions.getperformancereviewsFailure());
    }
}

export function* getpreviousreviewtasksRequest(api, action) {
    try {
        const { id, userId, payload } = action;
        payload.page = 1;
        payload.perPage = 500;
        payload.order = 'date_start';
        const options = queryString.stringify(decamelize(payload));
        const response = yield api.getPerformanceReviews(userId, options);

        if (response.ok) {
            const reviews = filter((x) => x.id !== id, response.data.data);
            let tasks = [];
            let trainings = [];

            if (reviews.length > 0) {
                const performance_id = reviews.map((item) => item.id);
                const soptions = queryString.stringify({ performance_id }, { arrayFormat: 'bracket' });
                const tasksRes = yield api.getLearnFeeds('tasks', userId, soptions);
                if (tasksRes.ok) tasks = tasksRes.data.data;
                const trainingRes = yield api.getLearnFeeds('training', userId, soptions);
                if (trainingRes.ok) trainings = trainingRes.data.data;
            }

            yield put(ManageActions.getpreviousreviewtasksSuccess({ tasks, trainings }));
        } else {
            yield put(ManageActions.getpreviousreviewtasksFailure());
        }
    } catch (e) {
        yield put(ManageActions.getpreviousreviewtasksFailure());
    }
}

export function* fetchcertificationsreportRequest(api, action) {
    try {
        const { target, payload } = action;
        const options = queryString.stringify(pickBy(isNotNull, payload), { arrayFormat: 'bracket' });
        const response = yield api.getCertificationReport(target, options);
        if (response.ok) {
            yield put(ManageActions.fetchcertificationsreportSuccess({ type: target, data: response.data }));
        } else {
            yield put(ManageActions.fetchcertificationsreportFailure());
        }
    } catch (e) {
        yield put(ManageActions.fetchcertificationsreportFailure());
    }
}

export function* fetchcareerreportsRequest(api, action) {
    try {
        const { target, userIds, payload } = action;
        let options = queryString.stringify(pickBy(isNotNull, payload));
        if (userIds.length > 0) {
            const _options = join(
                '&',
                userIds.map((id) => 'user_id[]=' + id)
            );
            options += '&' + _options;
        }
        const response = yield api.getCareerReports(target, options);

        if (response.ok) {
            yield put(ManageActions.fetchcareerreportsSuccess({ type: target, data: response.data }));
        } else {
            yield put(ManageActions.fetchcareerreportsFailure());
        }
    } catch (e) {
        yield put(ManageActions.fetchcareerreportsFailure());
    }
}

export function* fetchhireorientationreportRequest(api, action) {
    try {
        const { payload } = action;
        const response = yield api.postHireOrientationReport(payload);

        if (response.ok) {
            yield put(ManageActions.fetchhireorientationreportSuccess(response.data));
        } else {
            yield put(ManageActions.fetchhireorientationreportFailure());
        }
    } catch (e) {
        yield put(ManageActions.fetchhireorientationreportFailure());
    }
}

export function* getmanagerdashboardRequest(api, action) {
    try {
        const { companyId, dateStart, dateEnd } = action;
        const response = yield api.getManagerDashboard(companyId, dateStart, dateEnd);

        if (response.ok) {
            yield put(ManageActions.getmanagerdashboardSuccess(response.data));
        } else {
            yield put(ManageActions.getmanagerdashboardFailure());
        }
    } catch (e) {
        yield put(ManageActions.getmanagerdashboardFailure());
    }
}

export function* postquotaactualsRequest(api, action) {
    try {
        const { id, payload, after, callback } = action;
        const response = yield api.postQuotaActualsSave(payload);

        if (response.ok) {
            if (response.data.actuals) {
                if (after) yield put({ ...after });
                if (callback?.onRefresh) yield callback.onRefresh();
                yield put(ManageActions.postquotaactualsSuccess(id, response.data.actuals));
                if (!payload?.day) {
                    let modalData = yield select((state) => state.app.modalData);
                    if (modalData.before?.scorecards) {
                        let modalCallBack = yield select((state) => state.app.modalCallBack);
                        let _scorecards = clone(modalData.before.scorecards);
                        modalData = clone(modalData);
                        response.data.actuals.forEach((actual) => {
                            _scorecards = _scorecards.map((scorecard) => {
                                scorecard.quotas = scorecard.quotas.map((item) => {
                                    if (item.actuals && item.actuals.length > 0) {
                                        const idx = findIndex((x) => x.id == actual.id)(item.actuals);
                                        if (idx > -1) {
                                            item.actuals[idx] = actual;
                                        } else {
                                            if (actual.quota_id == item.id) item.actuals.push(actual);
                                        }
                                    } else {
                                        if (actual.quota_id == item.id) item.actuals.push(actual);
                                    }
                                    return item;
                                });
                                return scorecard;
                            });
                        });
                        modalData.before.scorecards = _scorecards;
                        yield put(
                            AppActions.modalRequest({
                                type: null,
                                data: modalData,
                                callBack: modalCallBack,
                            })
                        );
                    }
                }
            } else {
                toast.warn("You don't have permission to update these quotas", {
                    position: toast.POSITION.TOP_RIGHT,
                });
                yield put(ManageActions.postquotaactualsFailure());
            }
        } else {
            yield put(ManageActions.postquotaactualsFailure());
        }
    } catch (e) {
        yield put(ManageActions.postquotaactualsFailure());
    }
}

export function* getanniversarybirthdayRequest(api, action) {
    try {
        const { companyId, base, payload } = action;
        const options = queryString.stringify(payload);
        const response = yield api.getAnniversaryReports(companyId, base, options);

        if (response.ok) {
            for (let active of response.data.active.employees) {
                active.profile.avatar = convertUrl(active.profile.avatar, '/images/default.png');
            }
            for (let prior of response.data.prior.employees) {
                prior.profile.avatar = convertUrl(prior.profile.avatar, '/images/default.png');
            }
            for (let recruit of response.data.recruit.employees) {
                recruit.profile.avatar = convertUrl(recruit.profile.avatar, '/images/default.png');
            }

            const data = {
                active: response.data.active,
                prior: response.data.prior,
                recruit: response.data.recruit,
            };
            yield put(ManageActions.getanniversarybirthdaySuccess(data));
        } else {
            yield put(ManageActions.getanniversarybirthdayFailure());
        }
    } catch (e) {
        yield put(ManageActions.getanniversarybirthdayFailure());
    }
}

export function* posttrainingreportsRequest(api, action) {
    try {
        const { payload, target } = action;
        const response = yield api.postTrainingReport(payload, target);
        if (response.ok) {
            yield put(ManageActions.posttrainingreportsSuccess(response.data, target));
        } else {
            yield put(ManageActions.posttrainingreportsFailure());
        }
    } catch (e) {
        yield put(ManageActions.posttrainingreportsFailure());
    }
}

export function* postquotaactualcommentRequest(api, action) {
    try {
        const { payload } = action;
        const response = yield api.postQuotaActualComment(payload);
        if (response.ok) {
            yield put(ManageActions.postquotaactualcommentSuccess(response.data));
            let modalData = yield select((state) => state.app.modalData);
            let modalCallBack = yield select((state) => state.app.modalCallBack);
            modalData = clone(modalData);
            let scorecards = modalData.before.scorecards;
            scorecards = scorecards.map((scorecard) => {
                scorecard.quotas = scorecard.quotas.map((item) => {
                    if (item.actuals && item.actuals.length > 0) {
                        const idx = findIndex((x) => x.id == response.data.id)(item.actuals);
                        if (idx > -1) item.actuals[idx] = response.data;
                    }
                    return item;
                });
                return scorecard;
            });
            modalData.before.scorecards = scorecards;
            yield put(
                AppActions.modalRequest({
                    type: null,
                    data: modalData,
                    callBack: modalCallBack,
                })
            );
        } else {
            yield put(ManageActions.postquotaactualcommentFailure());
        }
    } catch (e) {
        yield put(ManageActions.postquotaactualcommentFailure());
    }
}

export function* postupdateactualcommentsRequest(api, action) {
    try {
        const { payload, after } = action;
        const response = yield api.postQuotaActualComments(payload);
        if (response.ok) {
            yield put(ManageActions.postupdateactualcommentsSuccess(response.data));
            if (after) yield put({ ...after });
        } else {
            yield put(ManageActions.postupdateactualcommentsFailure());
        }
    } catch (e) {
        yield put(ManageActions.postupdateactualcommentsFailure());
    }
}

export function* postsignoffRequest(api, action) {
    try {
        const { payload, completed, after } = action;
        const response = yield api.postSignOff(payload);

        if (response.ok && response.data.status == 'success') {
            yield put(ManageActions.postsignoffSuccess(response.data));

            const agreed = response.data.performance.data.agreed;
            if (completed || (agreed.supervisor && agreed.employee)) {
                let performance = completed || response.data.performance;
                performance = {
                    ...performance,
                    data: { ...performance.data, agreed: response.data.performance.data.agreed },
                    status: 2,
                };
                const res = yield api.postPerformanceReview({ performance });
                if (res.ok) {
                    if (after) yield put({ ...after });
                    yield call(NavigationService.goBack);
                    yield put(ManageActions.postperformancereviewSuccess(res.data));
                    toast.success('Your performance review has been completed!');
                } else {
                    toast.error('Your performance review has not been completed!');
                }
            } else {
                toast.success('Your email and password is correct!');
            }
        } else {
            yield put(ManageActions.postsignoffFailure());
            toast.error('Oops, Your email or password is incorrect!. Please try again', {
                position: toast.POSITION.TOP_RIGHT,
            });
        }
    } catch (e) {
        yield put(ManageActions.postsignoffFailure());
    }
}

export function* postquotareportRequest(api, action) {
    try {
        const { payload } = action;
        const response = yield api.postQuotaReports(payload);
        if (response.ok) {
            yield put(ManageActions.postquotareportSuccess(response.data));
        } else {
            yield put(ManageActions.postquotareportFailure());
        }
    } catch (e) {
        yield put(ManageActions.postquotareportFailure());
    }
}

export function* posttrainingcompetencyreportRequest(api, action) {
    try {
        const { payload } = action;
        const response = yield api.postTrainingCompetencyReports(payload);
        if (response.ok) {
            yield put(ManageActions.posttrainingcompetencyreportSuccess(response.data));
        } else {
            yield put(ManageActions.posttrainingcompetencyreportFailure());
        }
    } catch (e) {
        yield put(ManageActions.posttrainingcompetencyreportFailure());
    }
}

export function* postterminateemployeeRequest(api, action) {
    try {
        const { companyId, payload } = action;
        const response = yield api.postTerminateEmployee(companyId, payload);
        if (response.ok) {
            yield put(ManageActions.postterminateemployeeSuccess(response.data));
            yield put(AppActions.postmulticompanydataRequest());
        } else {
            yield put(ManageActions.postterminateemployeeFailure());
        }
    } catch (e) {
        yield put(ManageActions.postterminateemployeeFailure());
    }
}

export function* postindividualquotareportRequest(api, action) {
    try {
        const { payload, route } = action;
        const { userId, page, perPage, dateStart, dateEnd, filter } = payload;
        const newPayload = {
            page,
            perPage,
            dateStart,
            dateEnd,
            filter,
        };
        const response = yield api.postIndividualQuotaReport(userId, newPayload);
        if (response.ok) {
            yield put(ManageActions.postindividualquotareportSuccess(response.data));
            if (route) yield call(NavigationService.push, route);
        } else {
            yield put(ManageActions.postindividualquotareportFailure());
        }
    } catch (e) {
        yield put(ManageActions.postindividualquotareportFailure());
    }
}

export function* getquotasactualsRequest(api, action) {
    try {
        const { userId, payload } = action;
        const options = queryString.stringify(payload);
        const response = yield api.getQuotasActuals(userId, options);
        if (response.ok) {
            yield put(ManageActions.getquotasactualsSuccess(response.data));
        } else {
            yield put(ManageActions.getquotasactualsFailure());
        }
    } catch (e) {
        yield put(ManageActions.getquotasactualsFailure());
    }
}

export function* getcompanyorgchartRequest(api, action) {
    try {
        const { companyId } = action;
        const response = yield api.getCompanyOrgChart(companyId);
        if (response.ok) {
            yield put(ManageActions.getcompanyorgchartSuccess(response.data));
        } else {
            yield put(ManageActions.getcompanyorgchartFailure());
        }
    } catch (e) {
        yield put(ManageActions.getcompanyorgchartFailure());
    }
}

export function* getsaveactualsreportRequest(api, action) {
    try {
        const { payload } = action;
        const options = queryString.stringify(decamelize(payload), { arrayFormat: 'bracket' });
        const response = yield api.getSaveActualsReport(options);
        if (response.ok) {
            yield put(ManageActions.getsaveactualsreportSuccess(response.data));
        } else {
            yield put(ManageActions.getsaveactualsreportFailure());
        }
    } catch (e) {
        yield put(ManageActions.getsaveactualsreportFailure());
    }
}

export function* getcheckoutactualsRequest(api, action) {
    try {
        const { payload } = action;
        const options = queryString.stringify(decamelize(payload), { arrayFormat: 'bracket' });
        const response = yield api.getCheckoutActuals(options);
        if (response.ok) {
            yield put(ManageActions.getcheckoutactualsSuccess(response.data));
        } else {
            yield put(ManageActions.getcheckoutactualsFailure());
        }
    } catch (e) {
        yield put(ManageActions.getcheckoutactualsFailure());
    }
}

export function* getcheckoutorgchartRequest(api, action) {
    try {
        const { payload } = action;
        const options = queryString.stringify(decamelize(payload), { arrayFormat: 'bracket' });
        const response = yield api.getCheckoutOrgChart(options);
        if (response.ok) {
            yield put(ManageActions.getcheckoutorgchartSuccess(response.data));
        } else {
            yield put(ManageActions.getcheckoutorgchartFailure());
        }
    } catch (e) {
        yield put(ManageActions.getcheckoutorgchartFailure());
    }
}

export function* posttrainingtrackreportRequest(api, action) {
    try {
        const { payload } = action;
        const response = yield api.postTrackReports(payload);
        if (response.ok) {
            const tracks = Object.keys(response.data?.tracks)?.map((key) => {
                const track = response.data?.tracks[key];
                return { ...track, id: key };
            });
            const data = {
                totals: response.data.totals,
                individuals: values(response.data?.individuals),
                tracks,
            };
            yield put(ManageActions.posttrainingtrackreportSuccess(data));
        } else {
            yield put(ManageActions.posttrainingtrackreportFailure());
        }
    } catch (e) {
        yield put(ManageActions.posttrainingtrackreportFailure());
    }
}

export function* trainingtrackdetailreportRequest(api, action) {
    try {
        const { payload, route } = action;
        const { track, users } = payload;

        if (track?.data?.cards?.length > 0) {
            const templates = track.data.cards.map((card) => card.card_template_id || card.id);
            const options = `per_page=${templates.length}`;
            const response = yield api.postLibraryCardTemplates({ templates }, options);
            if (response.ok) {
                const result = { track: { ...track, cards: response.data.data }, users };
                yield put(ManageActions.trainingtrackdetailreportSuccess(result));
                if (route) yield call(NavigationService.push, route);
            } else {
                yield put(ManageActions.trainingtrackdetailreportFailure());
            }
        } else {
            yield put(ManageActions.trainingtrackdetailreportFailure());
        }
    } catch (e) {
        yield put(ManageActions.trainingtrackdetailreportFailure());
    }
}

export function* postteamactivityreportRequest(api, action) {
    try {
        const { payload } = action;
        const response = yield api.postTeamActivityReport(payload);
        if (response.ok) {
            yield put(ManageActions.postteamactivityreportSuccess(response.data.data));
        } else {
            yield put(ManageActions.postteamactivityreportFailure());
        }
    } catch (e) {
        yield put(ManageActions.postteamactivityreportFailure());
    }
}

export function* postdeletescorecardRequest(api, action) {
    try {
        const { payload, after } = action;
        const response = yield api.postDeleteScorecard(payload);

        if (response.ok) {
            if (after) yield put({ ...after });
            toast.success('The scorecards has been deleted successfully!', {
                position: toast.POSITION.TOP_RIGHT,
            });
            yield put(ManageActions.postdeletescorecardSuccess(payload));
        } else {
            yield put(ManageActions.postdeletescorecardFailure());
        }
    } catch (e) {
        yield put(ManageActions.postdeletescorecardFailure());
    }
}

export function* getdailyfeedRequest(api, action) {
    try {
        const { userId } = action;
        const response = yield api.getDailyFeed(userId);

        if (response.ok) {
            yield put(ManageActions.getdailyfeedSuccess(response.data));
        }
    } catch (e) {
        yield put(ManageActions.getdailyfeedFailure());
    }
}

export function* getcheckoutRequest(api, action) {
    try {
        const { payload, route } = action;
        const options = queryString.stringify(payload);
        const response = yield api.getCheckout(options);
        if (response.ok) {
            yield put(ManageActions.getcheckoutSuccess(response.data));
        } else {
            yield put(ManageActions.getcheckoutFailure());
        }
        if (route) yield call(NavigationService.push, route);
    } catch (e) {
        yield put(ManageActions.getcheckoutFailure());
    }
}

export function* postaddcheckoutcommentRequest(api, action) {
    try {
        const { payload } = action;

        const response = yield api.postAddCheckoutComment(payload);
        if (response.ok) {
            yield put(ManageActions.postaddcheckoutcommentSuccess(response.data.checkout));
            yield put(ManageActions.postcheckouteditRequest(response.data));
        } else {
            yield put(ManageActions.postaddcheckoutcommentFailure());
        }
    } catch (e) {
        yield put(ManageActions.postaddcheckoutcommentFailure());
    }
}

export function* postcheckouteditRequest(api, action) {
    try {
        const { payload } = action;

        const response = yield api.postCheckoutEdit(payload);
        if (response.ok) {
            yield put(ManageActions.postcheckouteditSuccess());
        } else {
            yield put(ManageActions.postcheckouteditFailure());
        }
    } catch (e) {
        yield put(ManageActions.postcheckouteditFailure());
    }
}

export function* postcheckoutsignoffRequest(api, action) {
    try {
        const { payload } = action;

        const response = yield api.postCheckoutSignOff(payload);
        if (response.ok) {
            yield put(ManageActions.postcheckoutsignoffSuccess(response.data));
        } else {
            yield put(ManageActions.postcheckoutsignoffFailure());
        }
    } catch (e) {
        yield put(ManageActions.postcheckoutsignoffFailure());
    }
}

export function* postcheckoutskipdayRequest(api, action) {
    try {
        const { payload, after, callback } = action;

        const response = yield api.postCheckoutSkipDay(payload);
        if (response.ok) {
            yield put(ManageActions.postcheckoutskipdaySuccess(response.data));
            if (after) yield put({ ...after });
            if (callback?.onRefresh) yield callback.onRefresh();
        } else {
            yield put(ManageActions.postcheckoutskipdayFailure());
        }
    } catch (e) {
        yield put(ManageActions.postcheckoutskipdayFailure());
    }
}

export function* postcheckoutresetRequest(api, action) {
    try {
        const { payload, after } = action;

        const response = yield api.postCheckoutReset(payload);
        if (response.ok) {
            yield put(ManageActions.postcheckoutresetSuccess(response.data));
            if (after) yield put({ ...after });
        } else {
            yield put(ManageActions.postcheckoutresetFailure());
        }
    } catch (e) {
        yield put(ManageActions.postcheckoutresetFailure());
    }
}

export function* postcheckoutapproveRequest(api, action) {
    try {
        const { payload } = action;

        const response = yield api.postCheckoutApprove(payload);
        if (response.ok) {
            yield put(ManageActions.postcheckoutapproveSuccess(response.data));
        } else {
            yield put(ManageActions.postcheckoutapproveFailure());
        }
    } catch (e) {
        yield put(ManageActions.postcheckoutapproveFailure());
    }
}
